import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTheme } from "@emotion/react";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import FilterListIcon from "@mui/icons-material/FilterList";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import RoomIcon from "@mui/icons-material/Room";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BIBlue, BIGrey, lightenHexColor } from "../../assets/helpers";
import JobPlaceHolder from "../../assets/images/job_placeholder.png";
import Loader from "../../common/Loader";
import {
  FETCH_EXTERNAL_JOBS,
  FETCH_JOB_POSTS_NEW_2,
  FETCH_JOB_POST_TYPES,
  GET_JOB_POST_DETAILS,
  SAVE_EXTERNAL_JOB_ALERTS,
  FETCH_USER_FAVORITE_JOB_POSTS,
  SAVE_USER_FAVORITE_JOB_POSTS,
  DELETE_USER_FAVORITE_JOB_POSTS
} from "../../constant/Apipath";
import { English } from "../../constant/EnglishFallback";
import { setJobSelectedLocation, setLogindata } from "../../redux/actions/actions";
import { Postrequestcall } from "../../request/Postrequest";
import AdContainer from "../common/AdContainer";
import Emptydata from "../common/Emptydata";
import Pageheader from "../common/Pageheader";
import SearchField from "../common/SearchField";
import FilterListModal from "./FilterListModal";
import "./Jobpostings.css";
import LocationMenuModal from "./LocationMenuModal";
import MyJobsModal from "./MyJobsModal";
import SaveAsAlertBanner from "./SaveAsAlertBanner";
import ViewJobPosting from "./ViewJobPosting";
import NoTradeModal from "./NoTrade";
import EditTrade from "../profile/EditTrade";
import {
  getJobFavoriteKey,
  getJobIdFromItem,
  getJobIsExternal,
  getExpiryLabel
} from "./favoriteUtils";

const formatJobTypeLabel = (value) => {
  if (!value) return "";
  const type = String(value).trim();
  const normalizedType = type.toUpperCase().replace(/[^A-Z]/g, "");
  const typeMap = {
    FULLTIME: "Full-Time",
    PARTTIME: "Part-Time",
    CONTRACT: "Contract"
  };

  return typeMap[normalizedType] || type;
};

const getJobMode = (job = {}) => job?.mode || job?.Mode || "";

const isExternalJob = (job = {}) => {
  if (job?.isExternal !== undefined) return Boolean(job?.isExternal);
  if (job?.IsExternal !== undefined) return Boolean(job?.IsExternal);

  return getJobMode(job).toLowerCase() === "external";
};

const getEmploymentType = (job = {}) =>
  job?.jobEmploymentType || job?.jobEmployementType || job?.jobType || job?.type || "";

const getTradeItems = (job = {}) => {
  const trades = [];
  const rawTrades = job?.trades ?? job?.Trades;
  const rawTradeNames = job?.tradeName || job?.TradeName || "";
  const tradeNames = rawTradeNames
    ? String(rawTradeNames)
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean)
    : [];

  if (Array.isArray(rawTrades)) {
    rawTrades.forEach((trade, index) => {
      if (!trade) return;

      if (typeof trade === "string" || typeof trade === "number") {
        const tradeId = String(trade);
        trades.push({
          id: tradeId,
          tradeId,
          tradeName: tradeNames[index] || ""
        });
        return;
      }

      trades.push(trade);
    });

    return trades;
  }

  if (rawTrades) {
    String(rawTrades)
      .split(",")
      .map((trade) => trade.trim())
      .filter(Boolean)
      .forEach((trade, index) => {
        trades.push({
          id: trade,
          tradeId: trade,
          tradeName: tradeNames[index] || ""
        });
      });
  }

  return trades;
};

const normalizeJob = (job = {}) => ({
  ...job,
  mode: getJobMode(job),
  isExternal: isExternalJob(job),
  jobEmploymentType: getEmploymentType(job),
  jobPostTypes: Array.isArray(job?.jobPostTypes) ? job?.jobPostTypes : [],
  trades: getTradeItems(job),
  jobId: job?.jobId || job?.JobId || job?.id || job?.Id || "",
  jobPostId: job?.jobPostId || job?.JobPostId || job?.id || job?.Id || ""
});

const normalizeJobs = (jobs = []) => jobs.map(normalizeJob);

// const CONSTANT = { firstTime: false };

function JobPostings() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const debounceTimeout = useRef(null);
  const favoriteToggledRef = useRef(false);

  // Redux selectors
  const { multiLanguageData, jobSelectedLocation } = useSelector((state) => state?.main);
  const loginData = useSelector((state) => state.main?.loginData);

  const [showNoTradeModal, setShowNoTradeModal] = useState(false);
  const [showEditTradeModal, setShowEditTradeModal] = useState(false);
  const [favoriteLoadingKeys, setFavoriteLoadingKeys] = useState({});

  // State needed for JobPostings (main list view)
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [keyboardTerm, setKeyboardTerm] = useState("");
  const [typeOptions, setTypeOptions] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [isNextDataCalled, setIsNextDataCalled] = useState(false);
  const [showMyJobsModal, setShowMyJobsModal] = useState(false);
  const [activeTab, setActiveTab] = useState(location?.state?.isExternal ? 1 : 0); // 0 for Internal, 1 for External
  const [showJobAlertDialog, setShowJobAlertDialog] = useState(false);
  const [isAlertSaving, setIsAlertSaving] = useState(false);
  const [categoryType, setCategoryType] = useState("");
  const selectedJobFavoriteKey = selectedJobDetails ? getJobFavoriteKey(selectedJobDetails) : null;
  const selectedJobIsFavoriteLoading = selectedJobFavoriteKey
    ? Boolean(favoriteLoadingKeys[selectedJobFavoriteKey])
    : false;

  // Language data
  const LanguageData = multiLanguageData?.JobPostings || {};
  const {
    TEXT_MY_POSTING_BTN = English.JobPostings.TEXT_MY_POSTING_BTN,
    TEXT_JOB_POSTING_HEADER = English.JobPostings.TEXT_JOB_POSTING_HEADER,
    TEXT_SEARCH_JOBS = English.JobPostings.TEXT_SEARCH_JOBS,
    TEXT_SELECT_LOCATION = English.JobPostings.TEXT_SELECT_LOCATION,
    TEXT_APPLIED = English.JobPostings.TEXT_APPLIED,
    TEXT_VIA = English.JobPostings.TEXT_VIA,
    TEXT_POSTED = English.JobPostings.TEXT_POSTED,
    TEXT_TODAY = English.JobPostings.TEXT_TODAY,
    TEXT_ONE_DAY_AGO = English.JobPostings.TEXT_ONE_DAY_AGO,
    TEXT_DAYS_AGO = English.JobPostings.TEXT_DAYS_AGO,
    TEXT_MORE = English.JobPostings.TEXT_MORE,
    TEXT_NO_JOB_POSTINGS_FOUND = English.JobPostings.TEXT_NO_JOB_POSTINGS_FOUND
  } = LanguageData || {};

  const headerName =
    location.pathname === "/my-postings" ? TEXT_MY_POSTING_BTN : TEXT_JOB_POSTING_HEADER;

  const fetchJobPostTypes = async () => {
    try {
      const obj = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: "",
        UserId: 1,
        IncludeRecordNr: "true",
        TypeOfObjectReturned: "",
        FetchAllowedRecordsOnly: true,
        SearchList: [],
        SortList: [{}]
      };
      const res = await Postrequestcall(FETCH_JOB_POST_TYPES, obj, loginData?.accessToken);
      const resData = res?.data?.data || [];
      const updatedData = resData?.map((value) => ({
        value: value.id,
        label: value.typeName
      }));

      const filteredTypes = [
        { value: "All", label: "All" },
        ...updatedData.filter((type) =>
          ["Full Time", "Part Time", "Contract"].some((allowed) =>
            type.label.toLowerCase().includes(allowed.toLowerCase())
          )
        )
      ];
      setTypeOptions(filteredTypes);
    } catch (error) {
      console.log("Error fetchJobPostTypes :: ", error);
    }
  };

  const setFavoriteLoading = (key, value) => {
    if (!key) return;
    setFavoriteLoadingKeys((prev) => ({ ...prev, [key]: value }));
  };



  const fetchJobById = async (id, isExternalNavigation) => {
    const stringifyId = (value) => {
      if (value === undefined || value === null) return "";
      const stringValue = typeof value === "string" ? value.trim() : String(value);
      return stringValue;
    };

    const jobIdString = stringifyId(id);
    if (!jobIdString) return;

    const isNumericValue = !Number.isNaN(Number(jobIdString));
    const internalJobIdValue = isNumericValue ? Number(jobIdString) : jobIdString;
    const isExternalJob = isExternalNavigation ?? true;

    setIsLoading(true);
    try {
      let response;
      let jobList = [];

      if (isExternalJob) {
        const externalPayload = {
          UserId: loginData?.userInformation?.UserId,
          JobId: jobIdString
        };
        response = await Postrequestcall(
          GET_JOB_POST_DETAILS,
          externalPayload,
          loginData?.accessToken
        );
        const externalData = response?.data?.data ?? response?.data ?? [];
        jobList = normalizeJobs(Array.isArray(externalData) ? externalData : externalData ? [externalData] : []);
      } else {
        const internalPayload = {
          UserId: loginData?.userInformation?.UserId,
          PageNumber: 1,
          PageSize: 20,
          Latitude: null,
          Longitude: null,
          RadiusKm: 0,
          SearchString: "",
          Types: "",
          Trades: "",
          Location: "",
          Category: "",
          JobPostId: internalJobIdValue
        };
        response = await Postrequestcall(
          FETCH_JOB_POSTS_NEW_2,
          internalPayload,
          loginData?.accessToken
        );
        const internalData = response?.data?.data || [];
        jobList = normalizeJobs(Array.isArray(internalData) ? internalData : internalData ? [internalData] : []);
      }

      if (!jobList.length) {
        return;
      }

      const candidatesMap = (jobItem) => [
        jobItem?.jobPostId,
        jobItem?.jobId,
        jobItem?.id,
        jobItem?.JobPostId,
        jobItem?.JobId,
        jobItem?.Id
      ];

      const jobToSelect =
        jobList.find((jobItem) => {
          const candidates = candidatesMap(jobItem);
          return candidates.some((candidate) => {
            const candidateString = stringifyId(candidate);
            if (!candidateString) return false;
            if (candidateString === jobIdString) return true;
            if (isNumericValue && Number(candidateString) === Number(jobIdString)) return true;
            return false;
          });
        }) || jobList[0];

      if (jobToSelect) {
        const resolvedIsExternal = Boolean(
          jobToSelect?.isExternal ?? jobToSelect?.IsExternal ?? isExternalJob(jobToSelect)
        );
        setSelectedJobDetails({ ...jobToSelect, isExternal: resolvedIsExternal });
        setShowJobDetailsModal(true);
      }
    } catch (error) {
      console.log("fetchJobById-> Error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllJobsExtAndInt = async (
    keyboardTerm = "",
    pageNumber = 1,
    selectedType = "All",
    selectedTrades = [],
    categoryValue = ""
  ) => {
    pageNumber === 1 && setIsLoading(true);
    try {
      // Find label for selectedType if it's an ID
      let normalizedType = "";
      if (selectedType && selectedType !== "All") {
        const typeOption = typeOptions.find((t) => t.value == selectedType);
        if (typeOption) {
          normalizedType = typeOption.label.trim().toLowerCase().replace(/[^a-z]/g, "");
        }
      }
      const getObj = {
        UserId: Number(loginData?.userInformation?.UserId),
        PageNumber: pageNumber,
        PageSize: 20,
        Latitude: jobSelectedLocation?.lat || null,
        Longitude: jobSelectedLocation?.lng || null,
        SearchString: keyboardTerm || "",
        Types: normalizedType,
        Trades: selectedTrades?.join(",") || "",
        Location: "",
        RadiusKm: jobSelectedLocation?.range || 0,
        Category: categoryValue || "",
        IncludeUserRecords: false,
        CompanyId: 0
      };

      const getJobSearchResponse = await Postrequestcall(
        FETCH_EXTERNAL_JOBS,
        getObj,
        loginData?.accessToken
      );

      if (getJobSearchResponse?.status === 201 || getJobSearchResponse?.status === 200) {
        let resData = getJobSearchResponse?.data?.data || [];
        resData = Array.isArray(resData) ? resData : resData ? [resData] : [];
        let totalRecords =
          getJobSearchResponse?.data?.nrOfRecords ||
          getJobSearchResponse?.data?.totalRecords ||
          resData?.[0]?.totalRecords ||
          0;
        let _updatedData = pageNumber === 1 ? normalizeJobs(resData) : [...jobs, ...normalizeJobs(resData)];
        setJobs(_updatedData || []);
        setHasMoreData(_updatedData.length < totalRecords);
      }
    } catch (error) {
      console.log(
        "fetchAllJobsExtAndInt-> Error When Getting External Job Search Results: ",
        error
      );
    } finally {
      setIsLoading(false);
      setIsNextDataCalled(false);
    }
  };

  useEffect(() => {
    fetchJobPostTypes();
    setPageNumber(1);
    fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);
    // console.log("Login Data: ", loginData);
  }, [jobSelectedLocation]);

  useEffect(() => {
    const ShowTradeModal = localStorage.getItem("ShowTradeModal");
    if ( ShowTradeModal == "false" || ShowTradeModal == null ){
      if( (loginData?.userInformation?.TradeName == "" || loginData?.userInformation?.TradeName == null)){
        localStorage.setItem("ShowTradeModal", true);
        setShowNoTradeModal(true);
      }else{
        if (!jobSelectedLocation?.address) {
        setShowLocationModal(true);
      }
      }
    } else {
      if (!jobSelectedLocation?.address) {
        setShowLocationModal(true);
      }
    }
  }, []);

  useEffect(() => {
    if (location?.state) {
      const { jobId, isExternal } = location.state;
      if (isExternal) {
        setActiveTab(1);
      }
      if (jobId) {
        fetchJobById(jobId, isExternal);
      }
      if (isExternal !== undefined || jobId) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state]);

  async function handleJobItemClick(event, job) {
    if (event) event.stopPropagation();
    setShowJobDetailsModal(true);
    // Mark as external if we're on the external tab
    setSelectedJobDetails(normalizeJob(job));
  }

  const handleSearchChange = (value) => {
    setKeyboardTerm(value);
    // setPageNumber(1);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchAllJobsExtAndInt(value, 1, selectedType, selectedTrades, categoryType);
    }, 500);
  };

  const onApplyFilters = ({ trades = [], types = "", keyword, categoryType: catType }) => {
    setSelectedTrades(trades);
    setSelectedType(types);
    if (catType !== undefined) setCategoryType(catType || "");
    setPageNumber(1);

    let newTerm = keyboardTerm;
    if (keyword !== undefined) {
      setKeyboardTerm(keyword);
      newTerm = keyword;
    }

    // Show the save-as-alert banner whenever at least one filter is active
    const hasActiveFilters =
      (trades && trades.length > 0) ||
      (types && types !== "" && types !== "All") ||
      (newTerm && newTerm.trim() !== "") ||
      (catType && catType !== "");
    setShowJobAlertDialog(hasActiveFilters);
    const appliedCategory = catType !== undefined ? catType : categoryType;
    fetchAllJobsExtAndInt(newTerm, 1, types, trades, appliedCategory);
  };

  const handleCreateJobAlert = async () => {
    setIsAlertSaving(true);
    try {
      let typeLabel = "";
      let typeLableId = selectedTrades && selectedTrades.length > 0 ? selectedTrades[0] : 0; // Assuming single selection for Job Type in alert
      if (selectedType && selectedType !== "All") {
        const typeOption = typeOptions.find((t) => t.value == selectedType);
        if (typeOption) {
          typeLabel = typeOption.label.toUpperCase().replace(/[^A-Z]/g, "");
        }
      }
      // Internal Job Alerts
      const payload = {
        UserId: loginData?.userInformation?.UserId || 0,
        ReturnRecordId: true,
        ReturnRecordError: true,

        SaveList: [
          {
            Id: 0,
            UserId: loginData?.userInformation?.UserId,
            CategoryType: categoryType || "",
            CategoryName: keyboardTerm?.trim() || "",
            JobType: typeLabel,
            JobTitleId: typeLableId,
            JobLatitude: jobSelectedLocation?.lat || null,
            JobLongitude: jobSelectedLocation?.lng || null,
            Radius: jobSelectedLocation?.range || 80,
            Location: jobSelectedLocation?.address || "",
            ModifiedBy: loginData?.userInformation?.UserId
          }
        ]
      };

      const res = await Postrequestcall(SAVE_EXTERNAL_JOB_ALERTS, payload, loginData?.accessToken);
      if (res.data?.status || res.status === 200) {
        setShowJobAlertDialog(false);
        // fetchJobs(searchTerm, 1, selectedTrades, selectedType, keyboardTerm);
        fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);
      }
    } catch (error) {
      console.error("Error creating job alert:", error);
    } finally {
      setIsAlertSaving(false);
    }
  };

  const onLocationApply = (locationData) => {
    dispatch(setJobSelectedLocation(locationData));
  };

  const handleNewAlertCreated = (isExternal) => {
    fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);
  };

  const handleFavoriteToggle = async (event, item) => {
    event?.stopPropagation();
    const key = getJobFavoriteKey(item);
    if (!key) return;
    const userId = Number(loginData?.userInformation?.UserId || 0);
    if (!userId) return;
    const jobId = getJobIdFromItem(item);
    if (!jobId) return;
    const isExternal = getJobIsExternal(item);
    const currentlyFavorite = item?.isFavorite;
    setFavoriteLoading(key, true);
    try {
      if (currentlyFavorite) {
        const payload = {
          UserId: userId,
          JobId: jobId.toString()
        };
        const res = await Postrequestcall(
          DELETE_USER_FAVORITE_JOB_POSTS,
          payload,
          loginData?.accessToken
        );
        if (res?.status) {
          setJobs((prev) => prev.map(j => getJobIdFromItem(j) === jobId ? { ...j, isFavorite: !currentlyFavorite } : j));
          if (selectedJobDetails && getJobIdFromItem(selectedJobDetails) === jobId) {
            setSelectedJobDetails((prev) => ({ ...prev, isFavorite: !currentlyFavorite }));
          }
          favoriteToggledRef.current = true;
        }
      } else {
        const payload = {
          UserId: userId,
          ReturnRecordId: true,
          ReturnRecordError: true,
          SaveList: [
            {
              Id: 0,
              UserId: userId,
              JobId: jobId.toString(),
              IsExternal: isExternal,
              ModifiedBy: userId
            }
          ]
        };
        const res = await Postrequestcall(
          SAVE_USER_FAVORITE_JOB_POSTS,
          payload,
          loginData?.accessToken
        );
        if (res?.status) {
          setJobs((prev) => prev.map(j => getJobIdFromItem(j) === jobId ? { ...j, isFavorite: !currentlyFavorite } : j));
          if (selectedJobDetails && getJobIdFromItem(selectedJobDetails) === jobId) {
            setSelectedJobDetails((prev) => ({ ...prev, isFavorite: !currentlyFavorite }));
          }
          favoriteToggledRef.current = true;
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    } finally {
      setFavoriteLoading(key, false);
    }
  };
  const handleModalCloseCheckRefetch = () => {
    if (favoriteToggledRef.current) {
      favoriteToggledRef.current = false;
      fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);
    }
  };
  const handleEditTradeSave = (updatedTrades = []) => {
    setShowEditTradeModal(false);
    setShowNoTradeModal(false);
    setPageNumber(1);
    fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);

    if (!loginData?.userInformation) return;
    const tradesForStore = Array.isArray(updatedTrades) ? updatedTrades : [];
    const updatedUserInfo = {
      ...loginData.userInformation,
      Trades: tradesForStore,
      TradesList: tradesForStore,
      TradeList: tradesForStore,
      trades: tradesForStore
    };
    dispatch(
      setLogindata({
        ...loginData,
        userInformation: updatedUserInfo
      })
    );
  };

  const handleLoadMore = async () => {
    if (hasMoreData && !isNextDataCalled) {
      setIsNextDataCalled(true);
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      fetchAllJobsExtAndInt(keyboardTerm, nextPage, selectedType, selectedTrades, categoryType);
    }
  };

  const renderFavoriteButton = (job) => {
    return (
      <IconButton
        size="small"
        onClick={(event) => handleFavoriteToggle(event, job)}
        sx={{ color: job?.isFavorite ? "#f5c518" : "#bdbdbd" }}>
        {job?.isFavorite ? (
          <StarIcon />
        ) : (
          <StarBorderIcon />
        )}
      </IconButton>
    );
  };

  const renderJobList = (itemList) => {
    return itemList.map((item, index) => {
      // Determine if this is an external job
      const isExternal = item?.isExternal;

      // Map fields based on job type
      const jobTitle = item?.title || item?.jobTitle;
      const companyName = item?.companyName || item?.employerName;
      const location = item?.location || item?.locationName;
      const postedDate = item?.jobPostedDate || item?.jobPostedAtDateTime || item?.dateCreated;
      const employmentTypeLabel = formatJobTypeLabel(getEmploymentType(item));
      const jobType = isExternal ? employmentTypeLabel : null;
      const publisher = isExternal ? item?.jobPublisher || item?.publisher : null;
      const employerLogo = isExternal ? item?.companyProfilePicture || item?.employerLogo : null;
      const employerWebsite = isExternal ? item?.employerWebsite || item?.employerWebsiteUrl : null;
      const jobTags = [
        ...(item?.jobPostTypes || []),
        ...(Array.isArray(item?.trades) ? item?.trades : [])
      ];
      const tradeTags = jobTags
        .map((tag) => tag?.typeName || tag?.tradeName || tag?.name || "")
        .filter(Boolean);

      // Calculate days ago for external jobs
      const daysAgo = isExternal ? moment().diff(moment(postedDate), "days") : null;
      // const expiryLabel = getExpiryLabel(item?.jobPostExpiryDate);

      return (
        <Card
          key={index.toString()}
          className="pagelist-content"
          sx={{
            marginTop: "0px",
            padding: 0,
            cursor: "pointer",
            backgroundColor: item?.isApplied ? "#f0f0f0" : "white"
          }}>
          <ListItem alignItems="flex-start" sx={{ m: 0, p: 0 }} key={index.toString()}>
            <ListItemButton
              alignItems="flex-start"
              sx={{ m: 0, display: "flex", gap: 2, p: 2 }}
              onClick={(event) => handleJobItemClick(event, item)}>
              {/* External Job UI */}
              {isExternal ? (
                <>
                  {/* Logo */}
                  <Avatar
                    src={employerLogo || ""}
                    alt={companyName}
                    variant="rounded"
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: employerLogo ? "transparent" : lightenHexColor(BIBlue, 90),
                      flexShrink: 0
                    }}>
                    {!employerLogo && <BusinessCenterIcon sx={{ color: BIBlue }} />}
                  </Avatar>

                  {/* Content */}
                  <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: 2 }} flex={1}>
                    <Stack spacing={0.5} flex={1}>
                      <Stack sx={{ justifyContent: "space-between", alignItems: "flex-start" }} direction="row">
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, fontSize: "1.1rem", lineHeight: 1.3 }}>
                          {jobTitle}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.95rem" }}>
                        {companyName}
                      </Typography>

                      {location && (
                        <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
                          <RoomIcon sx={{ fontSize: 16, color: "#666" }} />
                          <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
                            {location}
                          </Typography>
                        </Stack>
                      )}

                      {employerWebsite && (
                        <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                          <LinkIcon sx={{ fontSize: 16, color: "#666" }} />
                          <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
                            {employerWebsite.replace(/^https?:\/\/(www\.)?/, "")}
                          </Typography>
                        </Stack>
                      )}

                      <Stack sx={{ gap: 1,  alignItems: "center", mt: 0.5  }} direction="row" >
                        {publisher && (
                          <Chip
                            label={`${TEXT_VIA} ${publisher}`}
                            size="small"
                            sx={{
                              bgcolor: "#f0f0f0",
                              fontSize: "0.75rem",
                              height: 24
                            }}
                          />
                        )}
                        {jobTags?.map((tag, i) => (
                          <Chip
                            key={i}
                            label={tag?.typeName || tag?.tradeName || tag?.name || ""}
                            size="small"
                            sx={{
                              bgcolor: "#f0f0f0",
                              fontSize: "0.75rem",
                              height: 24
                            }}
                          />
                        ))}
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{ color: "#999", fontSize: "0.8rem", mt: 0.5 }}>
                        {TEXT_POSTED}{" "}
                        {daysAgo === 0
                          ? TEXT_TODAY
                          : daysAgo === 1
                            ? TEXT_ONE_DAY_AGO
                            : `${daysAgo} ${TEXT_DAYS_AGO}`}
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 0.5, alignItems: "flex-end", flexShrink: 0 }}>
                      <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
                        {renderFavoriteButton(item)}
                        {jobType && (
                          <Chip
                            label={jobType}
                            size="small"
                            sx={{
                              bgcolor: "#f0f0f0",
                              fontSize: "0.75rem",
                              height: 24
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" sx={{ color: BIGrey }}>
                        {moment(postedDate).format("MMMM DD, YYYY")}
                      </Typography>
                      {item?.isApplied && (
                        <Typography variant="body2" sx={{ color: BIGrey }}>
                          {TEXT_APPLIED}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </>
              ) : (
                /* Internal Job UI - Original Design */
                <ListItemText sx={{ gap: 1,  mt: 0, alignItems: "flex-start", flex: 1, alignItems: "center"  }} primary={ <Stack spacing={1} width={"100%"} direction={"row"} >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#e8edf8"
                        }}>
                        <BusinessCenterIcon style={{ fontSize: 30 }} sx={{ color: BIBlue }} />
                      </div>
                      <Stack
                               sx={{
                                 flexDirection: "row",
                                 justifyContent: "space-between",
                                 alignItems: "flex-start",
                                 width: "100%"
                               }}>
                               <Stack>
                                 <Typography
                                   variant="h6"
                                   maxWidth={"430px"}
                                   noWrap
                                   sx={{ color: item?.isApplied && BIGrey }}>
                                   {jobTitle}
                                 </Typography>
                                 {companyName && (
                                   <Typography
                                     variant="body2"
                                     maxWidth={"430px"}
                                     noWrap
                                     sx={{ color: item?.isApplied ? BIGrey : "black" }}>
                                     {companyName}
                                   </Typography>
                                 )}

                                 <Stack sx={{ gap: 0.5,  alignItems: "center", mt: 0.5  }} direction="row" flexWrap="wrap" >
                                   {tradeTags.slice(0, 5).map((tag, i) => (
                                     <Chip
                                       label={tag}
                                       key={i}
                                       size="small"
                                     />
                                   ))}
                                 </Stack>
                               </Stack>
                               <Stack sx={{ gap: 0.5,  alignItems: "flex-end", flexShrink: 0  }} >
                                 <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
                                   {renderFavoriteButton(item)}
                                   {employmentTypeLabel && (
                                     <Chip
                                       label={employmentTypeLabel}
                                       size="small"
                                       sx={{
                                         height: 24,
                                         fontSize: "0.75rem"
                                       }}
                                     />
                                   )}
                                 </Stack>
                                 <Typography
                                   variant="body2"
                                   maxWidth={"430px"}
                                   noWrap
                                   sx={{ color: BIGrey }}>
                                   {moment(postedDate).format("MMMM DD, YYYY")}
                                 </Typography>
                               </Stack>
                             </Stack>
                    </Stack>
                  }
                  secondary={
                    <Box>
                      <Stack sx={{ alignItems: "flex-start", justifyContent: "space-between" }} direction={"row"} spacing={2}>
                        <Stack>
                          {location && (
                            <Stack direction={"row"} spacing={0.5} sx={{ alignItems: "center" }}>
                              <LocationOnIcon sx={{ fontSize: 15 }} />
                              <Typography
                                variant="body2"
                                maxWidth={"430px"}
                                noWrap
                                sx={{ color: BIGrey }}>
                                {location}
                              </Typography>
                            </Stack>
                          )}

                          {/*
                            {expiryLabel && (
                              <Chip
                                label={expiryLabel}
                                size="small"
                                sx={{
                                  mt: 1,
                                  bgcolor: "#ffe8e8",
                                  color: "#c53030",
                                  fontWeight: 600
                                }}
                              />
                            )}
                          */}
                        </Stack>
                        {item?.isApplied && (
                          <Typography variant="body2" sx={{ color: BIGrey }}>
                            {TEXT_APPLIED}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  }
                />
              )}
            </ListItemButton>
          </ListItem>
        </Card>
      );
    });
  };

  return (
    <Box className="main-content-Box" sx={{ p: isSmallScreen ? 2 : 3, pt: 2 }}>
      <Pageheader headerName={headerName} />
      <Card className="main-content-card">
        <Stack sx={{ display: "flex", pb: 1.5,  justifyContent: "space-between" }} direction={"row"}>
          <Stack sx={{ width: "30%" }}>
            <SearchField
              size={"small"}
              value={keyboardTerm}
              onChange={handleSearchChange}
              placeholder={TEXT_SEARCH_JOBS}
            />
          </Stack>
          <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
            <Button
              startIcon={<RoomIcon />}
              size="large"
              variant="text"
              sx={{ textTransform: "none" }}
              onClick={() => {
                setShowLocationModal(true);
              }}>
              {jobSelectedLocation?.address
                ? `${jobSelectedLocation?.address} (${jobSelectedLocation?.range || 80} km)`
                : TEXT_SELECT_LOCATION}
            </Button>
            <IconButton
              color="primary"
              onClick={() => setShowFilter(true)}
              sx={{
                backgroundColor:
                  selectedTrades.length > 0 ||
                  (selectedType && selectedType !== "All") ||
                  keyboardTerm ||
                  keyboardTerm
                    ? lightenHexColor(BIBlue, 90)
                    : "transparent"
              }}>
              <FilterListIcon />
            </IconButton>
            <IconButton color="primary" onClick={() => setShowMyJobsModal(true)}>
              <PersonIcon />
            </IconButton>
          </Stack>
        </Stack>
        {/* Save-as-alert banner – slides down when active filters are applied */}
        <SaveAsAlertBanner
          visible={showJobAlertDialog}
          onYes={handleCreateJobAlert}
          onNo={() => setShowJobAlertDialog(false)}
          loading={isAlertSaving}
        />
        {isLoading ? (
          <Loader />
        ) : (
          <Box className="main-content-card-list" id="scrollablediv_prodcut">
            {jobs.length !== 0 ? (
              <InfiniteScroll
                dataLength={jobs?.length}
                next={handleLoadMore}
                hasMore={hasMoreData}
                loader={
                  hasMoreData && (
                    <h4 style={{ display: "flex", justifyContent: "center" }}>
                      <Loader />
                    </h4>
                  )
                }
                scrollableTarget="scrollablediv_prodcut">
                <List disablePadding>
                  <Stack sx={{ gap: 1 }} >{renderJobList(jobs)}</Stack>
                </List>
              </InfiniteScroll>
            ) : (
              <Box className="empty-data-card">
                <Emptydata
                  icon={
                    <img
                      src={JobPlaceHolder}
                      alt="JobPlaceHolder"
                      style={{ width: "500px", height: "400px", objectFit: "contain" }}
                    />
                  }
                  firstText={TEXT_NO_JOB_POSTINGS_FOUND}
                />
              </Box>
            )}
          </Box>
        )}
        <AdContainer />
      </Card>

      {showFilter && (
        <FilterListModal
          open={showFilter}
          onClose={() => setShowFilter(false)}
          selectedTrades={selectedTrades}
          selectedTypes={selectedType}
          keyboardTerm={keyboardTerm}
          searchTerm={keyboardTerm}
          onApplyFilters={onApplyFilters}
          isExternal={activeTab === 1}
          categoryType={categoryType}
        />
      )}

      {showLocationModal && (
        <LocationMenuModal
          open={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onLocationApply={onLocationApply}
          prevSelectedLocation={jobSelectedLocation || {}}
        />
      )}

      {showJobDetailsModal && (
        <ViewJobPosting
          loginData={loginData}
          open={showJobDetailsModal}
          onClose={() => setShowJobDetailsModal(false)}
          selectedJobDetails={selectedJobDetails || {}}
          jobApplied={(id) => {
            fetchAllJobsExtAndInt(keyboardTerm, 1, selectedType, selectedTrades, categoryType);
          }}
          from="jobPostings"
          isFavorite={selectedJobDetails?.isFavorite}
          onFavoriteToggle={(event) => handleFavoriteToggle(event, selectedJobDetails)}
        />
      )}

      <MyJobsModal
        visible={showMyJobsModal}
        onClose={() => {
          setShowMyJobsModal(false);
          handleModalCloseCheckRefetch();
        }}
        onNewAlertCreated={handleNewAlertCreated}
        onFavoriteModalClose={handleModalCloseCheckRefetch}
        onFavoriteToggle={handleFavoriteToggle}
        favoriteLoadingKeys={favoriteLoadingKeys}
      />

      <NoTradeModal
        open={showNoTradeModal}
        onClose={() => {
          jobSelectedLocation?.address ? setShowNoTradeModal(false) : setShowLocationModal(true);
          setShowNoTradeModal(false);
        }}
        onAddTrade={() => {
          setShowEditTradeModal(true);
        }}
      />

      {showEditTradeModal && (
        <EditTrade
          showTrade={showEditTradeModal}
          handleClose={() => setShowEditTradeModal(false)}
          setUsertrades={() => {}}
          tradesExisting={[]}
          userTradeList={[]}
          onDataSave={handleEditTradeSave}
        />
      )}
    </Box>
  );
}

export default JobPostings;

